import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Employee from '../../types/api/Employee';
import { 
    Person, 
    Phone, 
    CalendarToday, 
    AccessTime, 
    School, 
    Payment,
    CheckCircle,
    Cancel,
    Public
} from '@mui/icons-material';

interface EmployeeProfileProps {
    baseEmployee: Employee,
    closeDialog: ()=>void
}

export function EmployeeProfile(EmployeeProfileProps: EmployeeProfileProps) {
    const [employee, setEmployee] = useState<Employee>(EmployeeProfileProps.baseEmployee)
    const [showOldContracts, setShowOldContracts] = useState(false)
    const theme = useTheme();

    useEffect(() => {
        setEmployee(EmployeeProfileProps.baseEmployee);
    }, [EmployeeProfileProps.baseEmployee]);

    const contracts = employee.contrats ?? [];
    const isContractCurrent = (contract: any) => {
        if (!contract) return false;
        if (!contract.endDate) return true;
        const end = new Date(contract.endDate as any);
        if (isNaN(end.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        return end.getTime() > today.getTime();
    };

    const reversedContracts = [...contracts].reverse();
    const currentContract = employee.isActive ? reversedContracts.find((c) => isContractCurrent(c)) ?? null : null;
    const oldContractsSorted = currentContract
        ? reversedContracts.filter((c) => c.id !== currentContract.id)
        : reversedContracts;

    const formatDate = (date: Date | null) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('fr-FR');
    };

    const getDateContrat = (contrat: any) => {
        const hasEnd = contrat && contrat.endDate != null && !isNaN(new Date(contrat.endDate as any).getTime());
        if (hasEnd) {
            return `Du ${formatDate(contrat.startDate)} au ${formatDate(contrat.endDate)}`;
        }
        if (contrat && contrat.contratType === 'CDD') {
            return `Du ${formatDate(contrat.startDate)} au ${formatDate(contrat.endDate)}`;
        }
        return `Depuis le ${formatDate(contrat.startDate)}`;
    };

    const daysOfWeek = [
        { name: 'Lundi', noon: employee.MondayNoon, evening: employee.MondayEvening },
        { name: 'Mardi', noon: employee.TuesdayNoon, evening: employee.TuesdayEvening },
        { name: 'Mercredi', noon: employee.WednesdayNoon, evening: employee.WednesdayEvening },
        { name: 'Jeudi', noon: employee.ThursdayNoon, evening: employee.ThursdayEvening },
        { name: 'Vendredi', noon: employee.FridayNoon, evening: employee.FridayEvening },
        { name: 'Samedi', noon: employee.SaturdayNoon, evening: employee.SaturdayEvening },
        { name: 'Dimanche', noon: employee.SundayNoon, evening: employee.SundayEvening },
    ];

    const renderContractCard = (contrat: any, label?: string) => (
        <div
            className="p-3 rounded"
            style={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`
            }}
        >
            {label && (
                <p style={{ color: theme.palette.text.secondary }}>
                    {label}
                </p>
            )}
            <span style={{ color: theme.palette.text.primary }}>
                {contrat.contratType} - {employee.isFullTime ? 'Temps plein' : 'Temps partiel'}
            </span>
            <p style={{ color: theme.palette.text.secondary }}>
                {getDateContrat(contrat)}
            </p>
            <p style={{ color: theme.palette.text.secondary }}>
                Heures par mois : {contrat.hoursPerMonth}h
            </p>
            <p style={{ color: theme.palette.text.secondary }}>
                Taux horaire : {contrat.hourlyRate}€/h
            </p>
        </div>
    );

    return (
        <div 
            className="max-h-[90vh] overflow-y-auto"
            style={{ 
                minWidth: '800px',
                maxWidth: '1200px',
                width: '90vw'
            }}
        >
            {/* Header */}
            <div 
                className="sticky top-0 z-10 p-6"
                style={{ 
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}
            >
                <div className="flex items-center gap-4">
                    <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
                        style={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            color: theme.palette.primary.contrastText
                        }}
                    >
                        {employee.firstName[0]}{employee.lastName[0]}
                    </div>
                    <div>
                        <h1 
                            className='text-3xl font-bold'
                            style={{ color: theme.palette.text.primary }}
                        >
                            {employee.firstName} {employee.lastName}
                        </h1>
                        <p className="text-sm" style={{ color: theme.palette.text.secondary }}>
                            {employee.isActive ? (
                                <span className="flex items-center gap-1" style={{ color: theme.palette.success.main }}>
                                    <CheckCircle fontSize="small" /> Actif
                                </span>
                            ) : (
                                <span className="flex items-center gap-1" style={{ color: theme.palette.error.main }}>
                                    <Cancel fontSize="small" /> Inactif
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <div 
                className='flex flex-col gap-6 p-6'
                style={{ backgroundColor: theme.palette.background.default }}
            >
                {/* Informations générales */}
                <section 
                    className="rounded-lg p-5"
                    style={{ backgroundColor: theme.palette.background.paper }}
                >
                    <h2 
                        className="text-xl font-semibold mb-4 flex items-center gap-2"
                        style={{ color: theme.palette.text.primary }}
                    >
                        <Person /> Informations générales
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoItem icon={<Phone />} label="Téléphone" value={employee.phone || 'Non renseigné'} />
                        <InfoItem icon={<CalendarToday />} label="Date d'entrée" value={formatDate(employee.entryDate)} />
                        <InfoItem 
                            icon={<AccessTime />} 
                            label="Type de contrat" 
                            value={currentContract ? currentContract.contratType + (employee.isFullTime ? ' - Temps plein' : ' - Temps partiel') : 'Non renseigné'}
                        />
                        <InfoItem 
                            icon={<AccessTime />} 
                            label="Heures par semaine" 
                            value={`${employee.hoursPerWeek}h`} 
                        />
                        <InfoItem 
                            icon={<AccessTime />} 
                            label="Heures par mois" 
                            value={`${employee.hoursPerMonth}h`} 
                        />
                        <InfoItem 
                            icon={<Payment />} 
                            label="Taux horaire" 
                            value={`${employee.hourlyRate}€/h`} 
                        />
                    </div>
                </section>

                {/* Statut étudiant étranger */}
                {employee.isForeignStudent && (
                    <section 
                        className="rounded-lg p-5"
                        style={{ 
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.primary.main}`,
                            opacity: 0.9
                        }}
                    >
                        <h2 
                            className="text-xl font-semibold mb-4 flex items-center gap-2"
                            style={{ color: theme.palette.text.primary }}
                        >
                            <Public /> Étudiant étranger
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InfoItem 
                                label="Quota annuel" 
                                value={`${employee.foreignYearlyQuota}h`} 
                            />
                            <InfoItem 
                                label="Expiration du visa" 
                                value={formatDate(employee.visaExpirationDate)} 
                            />
                        </div>
                    </section>
                )}

                {/* Disponibilités hebdomadaires */}
                <section 
                    className="rounded-lg p-5"
                    style={{ backgroundColor: theme.palette.background.paper }}
                >
                    <h2 
                        className="text-xl font-semibold mb-4 flex items-center gap-2"
                        style={{ color: theme.palette.text.primary }}
                    >
                        <CalendarToday /> Disponibilités hebdomadaires
                    </h2>
                    <div className="overflow-x-auto">
                        <div className="flex gap-3 min-w-max pb-2">
                            {daysOfWeek.map((day) => (
                                <div 
                                    key={day.name} 
                                    className="flex flex-col items-center p-4 rounded min-w-[120px]"
                                    style={{ 
                                        backgroundColor: theme.palette.background.default,
                                        border: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <span 
                                        className="font-semibold mb-3 text-center"
                                        style={{ color: theme.palette.text.primary }}
                                    >
                                        {day.name}
                                    </span>
                                    <div className="flex flex-col gap-2 w-full">
                                        <AvailabilityBadge available={day.noon} label="Midi" theme={theme} />
                                        <AvailabilityBadge available={day.evening} label="Soir" theme={theme} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Formations */}
                {employee.formations && employee.formations.length > 0 && (
                    <section 
                        className="rounded-lg p-5"
                        style={{ backgroundColor: theme.palette.background.paper }}
                    >
                        <h2 
                            className="text-xl font-semibold mb-4 flex items-center gap-2"
                            style={{ color: theme.palette.text.primary }}
                        >
                            <School /> Formations
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {employee.formations.map((formation, index) => (
                                <div 
                                    key={index} 
                                    className="p-3 rounded"
                                    style={{ 
                                        backgroundColor: theme.palette.background.default,
                                        border: `1px solid ${theme.palette.divider}`
                                    }}
                                >
                                    <span style={{ color: theme.palette.text.primary }}>
                                        {formation.formation.label || `Formation ${index + 1}`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                {/*Contrats*/}
                {contracts.length > 0 && (
                    <section 
                        className="rounded-lg p-5"
                        style={{ backgroundColor: theme.palette.background.paper }}
                    >
                        <h2
                            className="text-xl font-semibold mb-4 flex items-center gap-2"
                            style={{ color: theme.palette.text.primary }}
                        >
                            <AccessTime /> Historique des contrats
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {currentContract && renderContractCard(currentContract, 'Contrat actuel')}
                        </div>

                            {oldContractsSorted.length > 0 && (
                            <div className="mt-4">
                                <button
                                    className="px-4 py-2 rounded-lg font-medium transition-colors"
                                    style={{
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText
                                    }}
                                    onClick={() => setShowOldContracts((previous) => !previous)}
                                >
                                    {showOldContracts ? 'Masquer les anciens contrats' : `Voir les anciens contrats (${oldContractsSorted.length})`}
                                </button>
                            </div>
                        )}

                        {showOldContracts && oldContractsSorted.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                                {oldContractsSorted.map((contrat, index) => (
                                    <div key={contrat.id ?? index}>
                                        {renderContractCard(contrat, 'Ancien contrat')}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {/* Bouton de fermeture */}
                <div 
                    className="flex justify-end pt-4"
                    style={{ borderTop: `1px solid ${theme.palette.divider}` }}
                >
                    <button
                        onClick={EmployeeProfileProps.closeDialog}
                        className="px-6 py-2 rounded-lg font-medium transition-colors"
                        style={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.palette.primary.dark}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.palette.primary.main}
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>    
    );
}

// Composant pour afficher une information avec icône
function InfoItem({ icon, label, value }: { icon?: React.ReactNode, label: string, value: string }) {
    const theme = useTheme();
    return (
        <div className="flex items-start gap-2">
            {icon && <span className="mt-1" style={{ color: theme.palette.text.secondary }}>{icon}</span>}
            <div>
                <p className="text-sm" style={{ color: theme.palette.text.secondary }}>{label}</p>
                <p className="font-medium" style={{ color: theme.palette.text.primary }}>{value}</p>
            </div>
        </div>
    );
}

// Composant pour afficher la disponibilité
function AvailabilityBadge({ available, label, theme }: { available: boolean, label: string, theme: any }) {
    return (
        <div 
            className="px-3 py-1 rounded text-sm font-medium"
            style={{
                backgroundColor: available 
                    ? theme.palette.success.main + '20'
                    : theme.palette.error.main + '20',
                color: available 
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                border: `1px solid ${available ? theme.palette.success.main : theme.palette.error.main}`
            }}
        >
            {available ? '✓' : '✗'} {label}
        </div>
    );
}

export default EmployeeProfile;